import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Button,
  Divider,
  Checkbox,
  LinearProgress,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: 12,
    maxWidth: 680,
    width: '100%',
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 8px',
    borderBottom: '1px solid #e2e8f0',
  },
  titleText: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#172b4d',
  },
  subTitle: {
    fontSize: '0.78rem',
    color: '#64748b',
  },
  content: {
    display: 'flex',
    gap: 16,
    padding: '16px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  left: {
    flex: 1,
    minWidth: 0,
  },
  right: {
    width: 180,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6,
  },
  notesField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.85rem',
    },
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 2,
  },
  checklistTitle: {
    fontSize: '0.85rem',
    flex: 1,
    color: '#172b4d',
  },
  checklistDone: {
    textDecoration: 'line-through',
    color: '#94a3b8',
  },
  addItemRow: {
    display: 'flex',
    gap: 6,
    marginTop: 6,
  },
  addItemField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.82rem',
    },
  },
  progress: {
    borderRadius: 4,
    marginBottom: 6,
  },
  progressLabel: {
    fontSize: '0.72rem',
    color: '#64748b',
    marginBottom: 3,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 12,
  },
  infoLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  infoValue: {
    fontSize: '0.82rem',
    color: '#172b4d',
  },
  actionBtn: {
    borderRadius: 6,
    textTransform: 'none',
    fontSize: '0.8rem',
    justifyContent: 'flex-start',
    marginBottom: 4,
    padding: '5px 10px',
  },
  viewTicketBtn: {
    borderRadius: 6,
    textTransform: 'none',
    fontSize: '0.8rem',
    marginTop: 4,
  },
}));

const KanbanCardModal = ({ ticket, open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();

  const [notes, setNotes] = useState(ticket.kanbanNote || '');
  const [checklists, setChecklists] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [dueDate, setDueDate] = useState(
    ticket.dueDate ? ticket.dueDate.substring(0, 10) : ''
  );

  const loadChecklist = useCallback(async () => {
    try {
      const { data } = await api.get(`/kanban/checklist/${ticket.id}`);
      setChecklists(data);
    } catch {
      // silent
    } finally {
      setLoadingChecklist(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    if (open) {
      loadChecklist();
    }
  }, [open, loadChecklist]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { kanbanNote: notes });
      toast.success('Nota salva');
    } catch {
      toast.error('Erro ao salvar nota');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveDueDate = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, { dueDate: dueDate || null });
      toast.success('Prazo atualizado');
    } catch {
      toast.error('Erro ao atualizar prazo');
    }
  };

  const handleToggleCheck = async item => {
    const updated = { checked: !item.checked };
    try {
      await api.put(`/kanban/checklist/${item.id}`, updated);
      setChecklists(prev =>
        prev.map(c => (c.id === item.id ? { ...c, ...updated } : c))
      );
    } catch {
      toast.error('Erro ao atualizar item');
    }
  };

  const handleAddItem = async () => {
    const title = newItem.trim();
    if (!title) return;
    try {
      const { data } = await api.post('/kanban/checklist', {
        ticketId: ticket.id,
        title,
        position: checklists.length,
      });
      setChecklists(prev => [...prev, data]);
      setNewItem('');
    } catch {
      toast.error('Erro ao adicionar item');
    }
  };

  const handleDeleteItem = async id => {
    try {
      await api.delete(`/kanban/checklist/${id}`);
      setChecklists(prev => prev.filter(c => c.id !== id));
    } catch {
      toast.error('Erro ao remover item');
    }
  };

  const checkedCount = checklists.filter(c => c.checked).length;
  const totalCount = checklists.length;
  const progressPct = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  const opportunityValue = (() => {
    const fields = ticket.contact?.extraInfo || [];
    const f = fields.find(x => x.name === 'valor');
    return f ? parseFloat(f.value) : null;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.paper }}
      onClick={e => e.stopPropagation()}
    >
      <div className={classes.titleBar}>
        <div>
          <Typography className={classes.titleText}>
            {ticket.contact?.name} — #{ticket.id}
          </Typography>
          {ticket.queue && (
            <Typography className={classes.subTitle}>
              Fila: {ticket.queue.name}
            </Typography>
          )}
        </div>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent style={{ padding: 0 }}>
        <div className={classes.content}>
          {/* LEFT COLUMN */}
          <div className={classes.left}>
            {/* Notes */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Descrição / Notas</Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                size="small"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Adicionar uma nota sobre este card..."
                className={classes.notesField}
              />
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                style={{ marginTop: 6, borderRadius: 6, textTransform: 'none', fontSize: '0.78rem' }}
              >
                {savingNotes ? 'Salvando...' : 'Salvar nota'}
              </Button>
            </div>

            <Divider style={{ marginBottom: 16 }} />

            {/* Checklist */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                Checklist {totalCount > 0 && `(${checkedCount}/${totalCount})`}
              </Typography>

              {totalCount > 0 && (
                <>
                  <Typography className={classes.progressLabel}>{progressPct}%</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressPct}
                    className={classes.progress}
                    color={progressPct === 100 ? 'primary' : 'secondary'}
                  />
                </>
              )}

              {loadingChecklist ? (
                <CircularProgress size={18} />
              ) : (
                checklists.map(item => (
                  <div key={item.id} className={classes.checklistItem}>
                    <Checkbox
                      size="small"
                      checked={item.checked}
                      onChange={() => handleToggleCheck(item)}
                      color="primary"
                      style={{ padding: 3 }}
                    />
                    <Typography
                      className={`${classes.checklistTitle} ${item.checked ? classes.checklistDone : ''}`}
                    >
                      {item.title}
                    </Typography>
                    <Tooltip title="Remover">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.id)}
                        style={{ padding: 3 }}
                      >
                        <DeleteIcon fontSize="small" style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                ))
              )}

              <div className={classes.addItemRow}>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Adicionar item..."
                  value={newItem}
                  onChange={e => setNewItem(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddItem()}
                  className={classes.addItemField}
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleAddItem}
                  style={{ borderRadius: 6, minWidth: 36, padding: '5px 10px' }}
                >
                  <AddIcon fontSize="small" />
                </Button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className={classes.right}>
            {/* Info */}
            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Contato</Typography>
              <Typography className={classes.infoValue}>
                {ticket.contact?.name || '—'}
              </Typography>
            </div>

            {ticket.contact?.number && (
              <div className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Telefone</Typography>
                <Typography className={classes.infoValue}>{ticket.contact.number}</Typography>
              </div>
            )}

            {ticket.user && (
              <div className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Atendente</Typography>
                <Typography className={classes.infoValue}>{ticket.user.name}</Typography>
              </div>
            )}

            {opportunityValue !== null && (
              <div className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Valor</Typography>
                <Typography className={classes.infoValue} style={{ color: '#0369a1', fontWeight: 700 }}>
                  R${' '}
                  {opportunityValue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </div>
            )}

            <div className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Criado em</Typography>
              <Typography className={classes.infoValue}>
                {ticket.createdAt
                  ? format(parseISO(ticket.createdAt), "dd/MM/yy 'às' HH:mm", { locale: ptBR })
                  : '—'}
              </Typography>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Due date */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Prazo</Typography>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                fullWidth
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                style={{ marginBottom: 6 }}
                inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
              />
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={handleSaveDueDate}
                style={{ borderRadius: 6, textTransform: 'none', fontSize: '0.78rem' }}
              >
                {dueDate ? 'Atualizar prazo' : 'Remover prazo'}
              </Button>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Open ticket */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<OpenInNewIcon />}
              className={classes.viewTicketBtn}
              onClick={() => {
                onClose();
                history.push(`/tickets/${ticket.uuid}`);
              }}
            >
              Ver Ticket
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KanbanCardModal;
